import { useTranslation } from '@thoughtspot/i18n';
import { Checkbox } from '@thoughtspot/radiant-react/widgets/checkbox';
import { Divider } from '@thoughtspot/radiant-react/widgets/divider';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Toggle } from '@thoughtspot/radiant-react/widgets/toggle';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { TranslationsKeys } from '../../../../../user-management/users/util';
import styles from './create-user-wizard.module.scss';
const DEBOUNCE_MS = 300;
export const ChecklistSection = ({ title, countLabel, items, loading, selectedIds, onSelectionChange, searchValue, onSearchChange, isServerSearch = false, optional = true, error, }) => {
    const { t } = useTranslation();
    const [localSearch, setLocalSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const debounceRef = useRef();
    const [showSelected, setShowSelected] = useState(false);
    useEffect(() => {
        if (isServerSearch)
            return undefined;
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(localSearch);
        }, DEBOUNCE_MS);
        return () => clearTimeout(debounceRef.current);
    }, [localSearch, isServerSearch]);
    const handleSearchInput = useCallback((_e, val) => {
        if (isServerSearch) {
            onSearchChange(val);
        }
        else {
            setLocalSearch(val);
        }
    }, [isServerSearch, onSearchChange]);
    const handleToggle = useCallback((id) => {
        onSelectionChange(prev => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    }, [onSelectionChange]);
    const filteredItems = useMemo(() => {
        if (isServerSearch || !debouncedSearch.trim())
            return items;
        const lower = debouncedSearch.toLowerCase();
        return items.filter(item => item.label.toLowerCase().includes(lower));
    }, [items, debouncedSearch, isServerSearch]);
    const displayItems = useMemo(() => showSelected
        ? filteredItems.filter(item => selectedIds.has(item.id))
        : filteredItems, [filteredItems, showSelected, selectedIds]);
    const selectedCount = selectedIds.size;
    useEffect(() => {
        if (selectedCount === 0)
            setShowSelected(false);
    }, [selectedCount]);
    const selectAll = useCallback(() => {
        onSelectionChange(prev => {
            const next = new Set(prev);
            filteredItems.forEach(item => next.add(item.id));
            return next;
        });
    }, [filteredItems, onSelectionChange]);
    const clearAll = useCallback(() => {
        onSelectionChange(prev => {
            const next = new Set(prev);
            filteredItems.forEach(item => next.delete(item.id));
            return next;
        });
    }, [filteredItems, onSelectionChange]);
    return (React.createElement(Vertical, null,
        React.createElement(Typography, { variant: "content-label-subhead", className: styles.checklistSectionTitle },
            title,
            optional && (React.createElement(Typography, { variant: "p-small", overrideTag: "span", color: Colors.grayLight }, ` ${t('Optional')}`))),
        React.createElement(Vertical, { spacing: "b" },
            React.createElement(Input.Text, { value: isServerSearch ? searchValue : localSearch, onChange: handleSearchInput, placeholder: t('cirrus.metadata.search.placeHolder'), prefixIcon: IconID.MAGNIFYING_GLASS, ariaLabel: t('cirrus.metadata.search.placeHolder'), "data-testid": `checklist-search-${title}` }),
            React.createElement(Horizontal, { className: styles.checklistHeader },
                React.createElement(Typography, { variant: "caption", color: error ? Colors.failure : undefined }, error || `${countLabel} (${selectedCount})`),
                React.createElement(Horizontal, { spacing: "b" },
                    React.createElement(Typography, { variant: "caption", color: Colors.accent, className: styles.createUserChecklistActionLink, onClick: selectAll }, t(TranslationsKeys.SELECT_ALL)),
                    React.createElement(Divider, { vertical: true, className: styles.createUserChecklistActionDivider }),
                    React.createElement(Typography, { variant: "caption", color: Colors.accent, className: styles.createUserChecklistActionLink, onClick: clearAll }, t(TranslationsKeys.CLEAR)))),
            React.createElement(LoadingIndicator.Global, { hideLoader: !loading },
                React.createElement(Vertical, { className: styles.checklistCard },
                    React.createElement(Vertical, { grow: true, className: styles.createUserCheckboxList }, displayItems.map(item => (React.createElement(Checkbox, { id: item.id, key: item.id, label: item.label, checked: selectedIds.has(item.id), isList: true, onChange: () => handleToggle(item.id), showTooltipOnEllipsis: true, tooltipContent: item.label, className: styles.listCheckbox })))),
                    React.createElement(Vertical, { className: styles.createUserShowSelectedToggle },
                        React.createElement(Toggle, { checked: showSelected, disabled: selectedCount === 0, onChange: (e) => setShowSelected(e.target.checked), labelPosition: "right", label: t(TranslationsKeys.SHOW_SELECTED), "data-testid": `showSelected-${title}` })))))));
};
//# sourceMappingURL=checklist-section.js.map